import {Component, OnDestroy, OnInit} from '@angular/core';
import {User} from '../../services/user/user';
import {BehaviorSubject} from 'rxjs';
import {ColDef, ColGroupDef, GridOptions} from 'ag-grid-community';
import {takeWhile} from 'rxjs/operators';
import {ReportsService} from '../../services/3080-reports/reports.service';
import {LoadingService} from '../../services/loading/loading.service';
import {HhgCarrierRateFilingService} from '../hhg-carrier-rate-filing/hhg-carrier-rate-filing.service';
import {AlertService} from '../../services/alert/alert.service';
import {Router} from '@angular/router';
import AppConstants from '../../shared/common-components/AppConstants';

@Component({
    selector: 'app-summary-report',
    templateUrl: './summary-report.component.html',
    styleUrls: ['./summary-report.component.css'],
    standalone: false
})
export class SummaryReportComponent implements OnInit, OnDestroy {
  private observer$ = new BehaviorSubject(true);
  currentUser: User | any;
  summaryReports: any;
  summaryReportsOptions: GridOptions = this.initializeGridOptions();
  private gridApi;
  columnDefs: any;
  public overlayNoRowsTemplate;
  rowData: any;
  currentYear: any = 0;
  years = [];
  excelStyles = [
    {
      id: 'textFormat',
      dataType: 'string',
    }
  ];

  constructor(private loader: LoadingService,
              private hhgService: HhgCarrierRateFilingService,
              private router: Router,
              private alertService: AlertService,
              private reportsService: ReportsService) {
    this.overlayNoRowsTemplate = '<span style="margin-top: 90px;">No results found. Please adjust your filters.</span>';
  }

  private initializeGridOptions() {
    return <GridOptions>{
      rowSelection: 'multiple',
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          suppressAndOrCondition: true
        }
      },
      pagination: true,
      headerHeight: 25,
      paginationPageSize: 25,
      cacheBlockSize: 25,
      rowData: this.summaryReports,
      overlayLoadingTemplate:
        '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>'
    };
  }

  /**
   * initializing the ag-grid
   * @param params
   */
  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridApi.setGroupHeaderHeight(35);
    // this.gridApi.setHeaderHeight(45);
    this.gridApi.setGridOption('headerHeight', 45);
  }

  ngOnInit() {
    this.currentYear = new Date().getFullYear();
    this.years.push({name: this.currentYear, value: this.currentYear});
    for (let i = 1; i < 7; i++) {
      const previousYr = this.currentYear - i;
      this.years.push({name: previousYr, value: previousYr});
    }
    this.onYearChange(this.currentYear);
  }

  updateColumnDefs(year) {
    this.columnDefs = this.getSummaryReportsResultsColumnDefs(year);
  }
  onYearChange(year) {
    this.loader.show();
    this.reportsService.getSurveyReport(year).pipe(takeWhile(() => this.observer$.value))
      .subscribe(reports => {
        this.summaryReports = reports;
        this.rowData = reports;
        this.loader.hide();
      }, error2 => {
        this.loader.hide();
        this.alertService.error('Error while getting reports');
      });
    this.updateColumnDefs(year);
  }

  getSummaryReportsResultsColumnDefs(year): (ColDef | ColGroupDef)[] {
    return [
      <ColDef>
        {
          headerName: 'Shipment Details',
          children: [
            {
              headerName: 'Survey #',
              field: 'surveyNumber',
              minWidth: 100,
              filter: 'agTextColumnFilter',
              cellRenderer: params => {
                if (AppConstants.isNotNullCondition(params)) {
                  return '<a tabindex="0">' + params.data.surveyNumber + '</a>';
                }
              },
              onCellClicked: event =>  this.onSurveyNo(event.data)
            },
            {
              headerName: 'FAIC Code',
              field: 'faicCode',
              width: 120,
              filter: 'agTextColumnFilter',
              cellClass: 'textFormat'
            },
            {
              headerName: 'Agency Name',
              field: 'agencyBureauName',
              width: 120,
              filter: 'agTextColumnFilter',
              cellRenderer: params => {
                if (AppConstants.isNotNullCondition(params.data) && AppConstants.isNotNullCondition(params.data.agencyBureauName)) {
                  return params.data.agencyBureauName.split('-')[0];
                }
              },
            },
            {
              headerName: 'Bureau Name',
              field: 'agencyBureauName',
              width: 120,
              filter: 'agTextColumnFilter',
              cellRenderer: params => {
                if (AppConstants.isNotNullCondition(params.data) && AppConstants.isNotNullCondition(params.data.agencyBureauName)) {
                  return params.data.agencyBureauName.split('-')[1];
                }
              }
            },
            {
              headerName: 'Shipment Type',
              field: 'shipmentType',
              width: 120,
              filter: 'agTextColumnFilter'
            },
            {
              headerName: 'Employee Last Name',
              field: 'employeeLastName',
              width: 120,
              filter: 'agTextColumnFilter'
            },
            {
              headerName: 'Employee Email',
              field: 'employeeEmail',
              width: 120,
              filter: 'agTextColumnFilter'
            },
            {
              headerName: 'Bill of Lading Number',
              field: 'bolNumber',
              width: 120,
              filter: 'agTextColumnFilter'
            },
            {
              headerName: 'BOL Type',
              field: 'bolType',
              width: 90,
              filter: 'agTextColumnFilter'
            },
            {
              headerName: 'SCAC Code',
              field: 'scacCode',
              width: 100,
              filter: 'agTextColumnFilter'
            },
            {
              headerName: 'Delivery Date',
              field: 'deliveryDate',
              width: 120,
              filter: 'agTextColumnFilter'
            }
          ]
        },
      {
        headerName: 'Employee Response',
        children: this.defineChildColumn(year)
      },
      {
        headerName: 'Comments',
        field: 'comments',
        width: 110,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Survey Status',
        field: 'surveyStatus',
        filter: 'agTextColumnFilter'
      }
    ];
  }

  defineChildColumn(year: any){
    return (year>2022) ? (
      [{
        headerName: 'Quality/Damage',
        field: 'quality',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Courtesy',
        field: 'courtesy',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Delivery',
        field: 'delivery',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Communication',
        field: 'communication',
        width: 150,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Problems',
        field: 'problems',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Recommendation',
        field: 'damage',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Over All',
        field: 'overall',
        width: 100,
        filter: 'agTextColumnFilter'
      }]
    ): (
      [{
        headerName: 'Quality',
        field: 'quality',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Damage',
        field: 'damage',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Courtesy',
        field: 'courtesy',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Delivery',
        field: 'delivery',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Communication',
        field: 'communication',
        width: 150,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Problems',
        field: 'problems',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Over All',
        field: 'overall',
        width: 100,
        filter: 'agTextColumnFilter'
      }]
    );

  }

  onSurveyNo(data) {
    // if (data.surveyStatus === 'Completed' || data.surveyStatus === 'Modified') {
    this.router.navigate(['/evaluationForm'],  { queryParams: { adminSurveyNo: data.surveyNumber } });
    // }
  }


  /**
   * Exporting the data in the csv format
   */
  onBtnExport() {
    const date = new Date();
    const params = {
      fileName: 'hhg-survey-report-' + date + '.csv',
      allColumns: true,
      columnSeparator: ','
    };
    this.gridApi.exportDataAsCsv(params);
  }
  ngOnDestroy(): void {
    this.observer$.next(false);
  }

}
